import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { notify } from "@/lib/notify";
import { ArrowLeft, History, Save } from "lucide-react";

import { api, type FeeSchedulePriceDraft } from "@/integrations/aws/resources";
import { useConfirm } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/app-shell";
import {
  FilterSelect,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useListSort,
  usePagination,
} from "@/components/list-controls";
import { useAuth } from "@/lib/auth";
import { todayISO } from "@/lib/fee-schedules";
import { cn } from "@/lib/utils";
import {
  feeSchedulePricesQuery,
  feeScheduleQuery,
  feeScheduleStandardQuery,
  feeScheduleVersionsQuery,
  invalidateStandardList,
  serviceCategoriesQuery,
} from "@/components/fee-schedules/queries";
import { effectiveFee, type FeePrice, type ServiceLite } from "@/components/fee-schedules/types";
import { BILLING_FREQ, FREQ_LABEL, type BillingFrequency } from "./services";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute(
  "/_authenticated/fee-schedules_/$scheduleId/versions/$effectiveFrom",
)({
  head: () => ({
    meta: [
      { title: "Dated prices — Somasi Ops" },
      {
        name: "description",
        content: "The price every service bills at over one period of a fee schedule.",
      },
    ],
  }),
  component: VersionDetailPage,
  errorComponent: () => <div className="text-destructive">Could not load these prices.</div>,
  notFoundComponent: () => <div className="text-muted-foreground">Prices not found.</div>,
});

type Filter = "all" | "priced" | "unpriced";

/** One service's pending edits, held until the page is saved. */
type Draft = {
  /** Whether this version prices the service at all. */
  priced?: boolean;
  fee?: number;
  billing_frequency?: BillingFrequency | null;
};

function VersionDetailPage() {
  const { scheduleId, effectiveFrom } = Route.useParams();
  const { canEdit } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: schedule, isLoading: scheduleLoading } = useQuery(feeScheduleQuery(scheduleId));
  const { data: versions = [], isLoading: versionsLoading } = useQuery(
    feeScheduleVersionsQuery(scheduleId),
  );
  const { data: prices = [], isLoading: pricesLoading } = useQuery(
    feeSchedulePricesQuery(scheduleId, effectiveFrom),
  );

  const { data: services = [] } = useQuery({
    queryKey: ["services_all"],
    queryFn: async () =>
      ((
        await api.services
          .list()
          .all()
          .select("id, code, name, category_id, default_fee, billing_frequency, active")
          .order("code")
      ).data ?? []) as ServiceLite[],
  });

  const version = versions.find((v) => v.effective_from === effectiveFrom) ?? null;

  /**
   * The set this one replaced: the schedule's own prices the day before it began.
   *
   * `versions` is newest first, so the first one starting earlier is the latest that
   * does. What a rate replaced is the comparison worth having while setting it — the
   * catalogue's list price says nothing about whether this is a rise or a cut.
   */
  const supersedes = useMemo(
    () => versions.find((v) => v.effective_from < effectiveFrom) ?? null,
    [versions, effectiveFrom],
  );

  /**
   * Whether this is the version billing today: the latest one that has started,
   * which is the same rule the resolver bills by. Not `is_latest` — a queued price
   * change makes the newest version one that has not started, and the version still
   * billing is then not the newest.
   */
  const billingNow = useMemo(
    () =>
      versions.find((v) => v.effective_from <= todayISO()) ?? versions[versions.length - 1] ?? null,
    [versions],
  );
  const isBillingNow = !!version && billingNow?.effective_from === version.effective_from;

  const { data: supersededPrices = [] } = useQuery({
    ...feeSchedulePricesQuery(scheduleId, supersedes?.effective_from ?? ""),
    enabled: !!supersedes,
  });

  const previousFees = useMemo(
    () =>
      new Map(
        (supersededPrices as FeePrice[]).map((price) => [price.service_id, effectiveFee(price)]),
      ),
    [supersededPrices],
  );

  /**
   * What the Somasi standard price list charges today — its version currently in
   * force, not the one in force on the day this set begins — what a service this set
   * does not price would otherwise bill at, and so the number a rate with nothing
   * before it is compared against.
   *
   * Empty when this schedule *is* the standard list: its own prices are what is being
   * set, and comparing them with themselves says nothing.
   */
  const { data: standard } = useQuery(feeScheduleStandardQuery());
  const standardFees = useMemo(
    () =>
      new Map(
        standard && standard.fee_schedule_id !== scheduleId
          ? standard.prices.map((price) => [price.service_id, price.fee])
          : [],
      ),
    [standard, scheduleId],
  );

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [filter, setFilter] = useState<Filter>("all");
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // Drafts belong to the version that was on screen when they were typed.
  useEffect(() => {
    setDrafts({});
  }, [effectiveFrom]);

  const discard = () => {
    setDrafts({});
    setNotes(version?.notes ?? "");
  };

  const dirty = Object.keys(drafts).length;

  // The version's own note, edited here and saved with the prices. It is a property
  // of the dated set — why these rates, who agreed them — so it belongs on the page
  // that shows the set rather than behind a separate dialog.
  const [notes, setNotes] = useState("");
  const notesDirty = !!version && notes !== (version.notes ?? "");

  // Seeded when the version arrives and whenever it changes underneath, but never
  // while there is an unsaved edit in the box.
  useEffect(() => {
    if (notesDirty) return;
    setNotes(version?.notes ?? "");
  }, [version?.effective_from, version?.notes, notesDirty]);

  const unsaved = dirty > 0 || notesDirty;

  // Leaving with unsaved prices loses them, and the browser is the only thing
  // that can intercept a tab close.
  useEffect(() => {
    if (!unsaved) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);

  const activeServices = useMemo(() => services.filter((s) => s.active), [services]);

  const { data: categories = [] } = useQuery(serviceCategoriesQuery());

  // Only categories something in the catalogue actually belongs to. Offering one that
  // filters to nothing reads as a broken filter rather than an empty category.
  const categoriesInUse = useMemo(() => {
    const ids = new Set(activeServices.map((s) => s.category_id).filter(Boolean));
    return categories.filter((c) => ids.has(c.id));
  }, [categories, activeServices]);

  const hasUncategorised = useMemo(
    () => activeServices.some((s) => !s.category_id),
    [activeServices],
  );

  const priceByService = useMemo(() => {
    const m = new Map<string, FeePrice>();
    (prices as FeePrice[]).forEach((p) => m.set(p.service_id, p));
    return m;
  }, [prices]);

  /**
   * Whether this version prices the service — as saved, not as typed.
   *
   * The tag, the filters and the count all read this one, so what the page says a
   * service's status is stays true until a save makes it true. A price typed into a
   * row is a pending change, and claiming the service was priced the moment a key was
   * pressed meant the page asserted something the schedule did not yet do.
   */
  const isPriced = (serviceId: string) => priceByService.has(serviceId);

  /** What it will be once saved — what the editor is about to write. */
  const willBePriced = (serviceId: string) =>
    drafts[serviceId]?.priced ?? priceByService.has(serviceId);

  /**
   * What a row shows, and what it would save at if only its frequency were set.
   *
   * A service this set does not price yet falls back to the rate it was on before
   * this set began, and only then to the standard fee. Adding a service back to a
   * version should carry its own rate forward rather than silently reset it to the
   * list price.
   */
  const feeFor = (svc: ServiceLite) => {
    const draft = drafts[svc.id];
    if (draft?.fee !== undefined) return draft.fee;
    const line = priceByService.get(svc.id);
    if (line) return effectiveFee(line);
    return previousFor(svc);
  };

  const freqFor = (svc: ServiceLite): BillingFrequency | null => {
    const draft = drafts[svc.id];
    if (draft?.billing_frequency !== undefined) return draft.billing_frequency;
    return priceByService.get(svc.id)?.billing_frequency ?? null;
  };

  const edit = (serviceId: string, patch: Draft) =>
    setDrafts((d) => ({ ...d, [serviceId]: { ...d[serviceId], ...patch } }));

  /**
   * The standard fee: the standard list's rate, or the service's own stored fee where
   * that list does not price it — the same rule as the Services page's column.
   */
  const standardFor = (svc: ServiceLite) =>
    standardFees.get(svc.id) ?? Number(svc.default_fee ?? 0);

  /** The rate before this set, or the standard fee when the set before did not price it. */
  function previousFor(svc: ServiceLite) {
    return previousFees.get(svc.id) ?? standardFor(svc);
  }

  // Starts on "In this version", which puts the priced services first: they are what
  // this version actually sets, and leaving them scattered through a hundred-odd rows
  // meant paging through services this schedule does not price to check the few it does.
  const sort = useListSort({ column: "status", ascending: true });

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matching = activeServices.filter((s) => {
      if (filter === "priced" && !isPriced(s.id)) return false;
      if (filter === "unpriced" && isPriced(s.id)) return false;
      if (category === "__none" && s.category_id) return false;
      if (category !== "all" && category !== "__none" && s.category_id !== category) return false;
      if (!q) return true;
      return s.code.toLowerCase().includes(q) || s.name.toLowerCase().includes(q);
    });

    // Every key reads the saved version, never `drafts`: a row being edited keeps its
    // place instead of moving out from under the cursor — which is what happened when
    // typing a price moved it off the "Not priced here" list mid-edit. It moves once
    // the save makes the move true.
    const savedFee = (s: ServiceLite) => {
      const line = priceByService.get(s.id);
      return line ? effectiveFee(line) : previousFor(s);
    };
    const key = (s: ServiceLite): string | number => {
      switch (sort.sort.column) {
        case "status":
          return isPriced(s.id) ? 0 : 1;
        case "name":
          return s.name.toLowerCase();
        case "previous":
          return previousFor(s);
        case "price":
          return savedFee(s);
        default:
          return s.code;
      }
    };
    const dir = sort.sort.ascending ? 1 : -1;
    // Ties keep catalogue order, so a sort on a column most rows share still reads
    // in the order the services are coded.
    return [...matching].sort((a, b) => {
      const ka = key(a);
      const kb = key(b);
      if (ka < kb) return -dir;
      if (ka > kb) return dir;
      return a.code.localeCompare(b.code);
    });
  }, [
    activeServices,
    search,
    filter,
    category,
    priceByService,
    previousFees,
    standardFees,
    sort.sort,
  ]);

  const pager = usePagination({ resetKey: [filter, category, search, sort.sort] });
  const page = useMemo(
    () => shown.slice(pager.offset, pager.offset + pager.pageSize),
    [shown, pager.offset, pager.pageSize],
  );

  const pricedCount = activeServices.filter((s) => isPriced(s.id)).length;
  /**
   * Writes the whole snapshot in one request.
   *
   * `PUT .../lines` replaces the version's set, so the payload is every service
   * this version should price — not a diff. That is what makes a save atomic:
   * the server works out the inserts, the updates and the drops, and a failure
   * leaves the version exactly as it was rather than halfway between two price
   * lists.
   */
  const save = async () => {
    // The page only renders the grid once the version has loaded, but this closure is
    // built before that check, so the guard is restated rather than asserted away.
    if (!version) return;

    setSaving(true);
    try {
      const payload: FeeSchedulePriceDraft[] = [];
      for (const svc of activeServices) {
        if (!willBePriced(svc.id)) continue;
        const fee = feeFor(svc);
        if (!Number.isFinite(fee) || fee < 0) {
          throw new Error(`“${svc.name}” needs a price of zero or more`);
        }
        const existing = priceByService.get(svc.id);
        payload.push({
          // Everything the line already carried — a negotiated minimum, a
          // markup — is sent back, so a save through this grid does not quietly
          // drop columns it has no field for.
          ...(existing
            ? {
                min_fee: existing.min_fee,
                max_fee: existing.max_fee,
                discount_pct: existing.discount_pct,
                markup_pct: existing.markup_pct,
                notes: existing.notes,
              }
            : {}),
          service_id: svc.id,
          standard_fee: fee,
          // `special_fee` wins in the resolver, so a line carrying one would bill
          // a number this editor never showed. Writing a price clears it.
          special_fee: null,
          billing_frequency: freqFor(svc),
          active: true,
        });
      }

      // Only what actually changed is written. Re-sending a hundred prices because a
      // note was corrected would rewrite their timestamps for nothing.
      if (dirty > 0) {
        const { error } = await api.feeSchedules.replacePrices(scheduleId, effectiveFrom, payload);
        if (error) throw new Error(error.message);
      }
      if (notesDirty) {
        const { error } = await api.feeSchedules.updateVersion(scheduleId, effectiveFrom, {
          notes: notes.trim() || null,
        });
        if (error) throw new Error(error.message);
      }

      setDrafts({});
      const saved =
        dirty > 0
          ? `${payload.length} ${payload.length === 1 ? "price" : "prices"} saved`
          : "Note saved";
      // Saving a set that has already been in force is allowed, but it is not the
      // same act as pricing a future period: it changes what invoices dated in that
      // period resolve to. Reporting it as a plain success would hide that.
      if (version.is_historical && dirty > 0) {
        notify.warning(saved, {
          description: `These prices applied from ${version.effective_from}, so invoices in that period now resolve differently.`,
        });
      } else {
        notify.success(saved);
      }
      qc.invalidateQueries({ queryKey: ["fee_schedule_prices", scheduleId, effectiveFrom] });
      qc.invalidateQueries({ queryKey: ["fee_schedule_versions", scheduleId] });
      invalidateStandardList(qc);
    } catch (err) {
      notify.error(err);
    } finally {
      setSaving(false);
    }
  };

  const leave = async () => {
    if (
      unsaved &&
      !(await confirm({
        title: "Leave without saving?",
        description:
          dirty > 0
            ? `${dirty} price ${dirty === 1 ? "change" : "changes"} on this page will be lost.`
            : "The note you changed will be lost.",
        confirmLabel: "Discard changes",
        tone: "warning",
      }))
    ) {
      return;
    }
    navigate({ to: "/fee-schedules/$scheduleId", params: { scheduleId } });
  };

  if (scheduleLoading || versionsLoading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (!schedule || !version) {
    return (
      <Alert variant="destructive">
        <AlertTitle>These prices were not found</AlertTitle>
        <AlertDescription>
          The version may have been deleted. Go back to{" "}
          <Link to="/fee-schedules" className="underline">
            the list of schedules
          </Link>
          .
        </AlertDescription>
      </Alert>
    );
  }

  const period = version.effective_to
    ? `${version.effective_from} → ${version.effective_to}`
    : `${version.effective_from} → open`;

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title={`Prices from ${version.effective_from}`}
          description={`${schedule.name} · ${period}`}
          breadcrumbs={[
            { label: "Fee Schedules", to: "/fee-schedules" },
            { label: schedule.name, to: `/fee-schedules/${scheduleId}` },
            { label: version.effective_from },
          ]}
          actions={
            <>
              {canEdit && (
                <>
                  {unsaved && (
                    <Button variant="ghost" onClick={discard} disabled={saving}>
                      Discard
                    </Button>
                  )}
                  <Button onClick={save} disabled={saving || !unsaved}>
                    <Save className="mr-2 h-4 w-4" />
                    {saving
                      ? "Saving…"
                      : dirty > 0
                        ? `Save ${dirty} change${dirty === 1 ? "" : "s"}`
                        : notesDirty
                          ? "Save note"
                          : "Saved"}
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={leave}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to versions
              </Button>
            </>
          }
        />

        <div className="mb-4 flex flex-wrap items-center gap-2">
          {version.is_latest ? (
            <Badge>Newest version</Badge>
          ) : (
            <Badge variant="outline">Superseded</Badge>
          )}
          {isBillingNow ? (
            <Badge variant="secondary">
              Billing now · {version.effective_from} → {version.effective_to ?? "open"}
            </Badge>
          ) : version.is_historical ? (
            <Badge variant="secondary">
              Was in force {version.effective_from} → {version.effective_to ?? "open"}
            </Badge>
          ) : (
            <Badge variant="secondary">Starts {version.effective_from}</Badge>
          )}
          <span className="text-sm text-muted-foreground">
            {pricedCount} of {activeServices.length} services priced · USD
          </span>
        </div>

        {/*
        Editing a version that has already started may change what invoices were
        raised at. It is allowed — a rate agreed late has to be correctable — but
        it is not the same act as pricing a future period, so it is said plainly
        rather than discovered later. Every change is recorded in the audit log.
      */}
        {version.is_historical && (
          <Alert className="mb-4">
            <History className="h-4 w-4" />
            <AlertTitle>These prices have already been in force</AlertTitle>
            <AlertDescription>
              They applied from {version.effective_from}
              {version.effective_to ? ` to ${version.effective_to}` : " onwards"}, so invoices may
              already have been raised at them. Changing them now changes what those invoices would
              resolve to. To reprice from a date going forward, add a new dated set on the schedule
              instead — this edit is recorded either way.
            </AlertDescription>
          </Alert>
        )}

        {!version.is_historical && (
          <Alert className="mb-4">
            <AlertTitle>Not billing yet</AlertTitle>
            <AlertDescription>
              These prices take over on {version.effective_from}. Until then invoices bill at the
              version in force, so this one can be edited freely.
            </AlertDescription>
          </Alert>
        )}

        {canEdit && (
          <div className="mb-4 rounded-md border bg-card p-3">
            <Label htmlFor="version-notes" className="text-xs text-muted-foreground">
              Note for these prices
            </Label>
            <Textarea
              id="version-notes"
              className="mt-1.5"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why these rates, and who agreed them."
            />
          </div>
        )}

        <ListToolbar>
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder="Search service code or name…"
          />
          <FilterSelect
            value={filter}
            onChange={(v) => setFilter(v as Filter)}
            options={[
              { value: "all", label: "All services" },
              { value: "priced", label: "Priced here" },
              { value: "unpriced", label: "Not priced here" },
            ]}
          />
          <FilterSelect
            value={category}
            onChange={setCategory}
            className="w-56"
            options={[
              { value: "all", label: "All categories" },
              ...categoriesInUse.map((c) => ({ value: c.id, label: c.name })),
              ...(hasUncategorised ? [{ value: "__none", label: "Uncategorised" }] : []),
            ]}
          />
        </ListToolbar>
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              <SortHeader sort={sort} column="name">
                Service
              </SortHeader>
              <SortHeader sort={sort} column="status" className="w-32">
                In this version
              </SortHeader>
              <TableHead className="w-40">Frequency</TableHead>
              <SortHeader sort={sort} column="previous" className="w-32 text-right" align="right">
                {supersedes ? (
                  "Replaces"
                ) : (
                  <span
                    title={
                      standard
                        ? `${standard.name}'s current rate${
                            standard.effective_from
                              ? ` (version from ${standard.effective_from})`
                              : ""
                          }`
                        : undefined
                    }
                  >
                    Standard fee
                  </span>
                )}
              </SortHeader>
              <SortHeader sort={sort} column="price" className="w-44 text-right" align="right">
                Price (USD)
              </SortHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pricesLoading ? (
              <TableMessageRow colSpan={5}>Loading prices…</TableMessageRow>
            ) : page.length === 0 ? (
              <TableMessageRow colSpan={5}>No services match.</TableMessageRow>
            ) : (
              page.map((svc) => {
                // What the version says today, and what this page is about to make it.
                const priced = isPriced(svc.id);
                const pending = willBePriced(svc.id);
                const changed = !!drafts[svc.id];
                return (
                  <TableRow
                    key={svc.id}
                    // The row's colour says one thing only: this row has been edited
                    // and not yet saved. Whether the version prices the service is the
                    // tag's job, and a tint for that as well made the two unreadable
                    // against each other.
                    className={cn(
                      changed && "border-l-2 border-l-primary bg-primary/10 hover:bg-primary/15",
                    )}
                  >
                    <TableCell>
                      <div className="font-medium">{svc.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{svc.code}</div>
                    </TableCell>
                    <TableCell>
                      {/*
                        A tag rather than a tinted row. Which services a version covers
                        is chosen when the version is created, so this reports a fact
                        rather than offering a choice — and a label says what it means,
                        where a background colour has to be learned.
                      */}
                      <div className="flex flex-col items-start gap-1">
                        {priced ? (
                          <Badge variant="secondary">Priced here</Badge>
                        ) : (
                          <Badge variant="outline" title="Falls through to the level below">
                            Not priced
                          </Badge>
                        )}
                        {/*
                          Said separately, and only while it is true: the tag is what
                          the version does, this is what saving would change it to.
                        */}
                        {changed && pending !== priced && (
                          <span className="text-[11px] text-muted-foreground">
                            {pending ? "will be priced" : "will be removed"}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={freqFor(svc) ?? "__inherit"}
                        disabled={!canEdit}
                        // Setting a frequency prices the service here, exactly as
                        // setting a fee does. Leaving it editable only once the row
                        // was priced made "how often" a decision you could reach only
                        // through "how much", even when the rate was the one thing not
                        // changing.
                        onValueChange={(v) =>
                          edit(svc.id, {
                            billing_frequency: v === "__inherit" ? null : (v as BillingFrequency),
                            priced: true,
                          })
                        }
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/*
                            Named rather than bare: "(inherit)" left the reader to
                            guess what it would bill at, when the catalogue already
                            says.
                          */}
                          <SelectItem value="__inherit">
                            Inherit · {FREQ_LABEL[svc.billing_frequency]}
                          </SelectItem>
                          {BILLING_FREQ.map((f) => (
                            <SelectItem key={f} value={f}>
                              {FREQ_LABEL[f]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                      USD {previousFor(svc).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      {supersedes && previousFees.get(svc.id) === undefined && (
                        // The set before this one did not price the service, so there
                        // is no rate being replaced; this is the standard fee.
                        <div className="text-[10px] opacity-70">standard</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-xs text-muted-foreground">USD</span>
                        <NumCell
                          disabled={!canEdit}
                          // Emptying the box is how a service comes off this version,
                          // now that there is no tick to clear. It reads the same way
                          // round as pricing does: a number means priced, nothing
                          // means it falls through to the level below.
                          onClear={pending ? () => edit(svc.id, { priced: false }) : undefined}
                          value={pending ? feeFor(svc) : null}
                          // Greyed, the rate this row would take if priced — so
                          // typing is an edit to a number already on screen rather
                          // than filling a blank.
                          placeholder={String(feeFor(svc))}
                          // Setting a price *is* pricing it. Asking the user to tick
                          // a box first and then type made "priced here" a second
                          // step they could forget, leaving a number typed against a
                          // service this version does not price.
                          onChange={(v) => edit(svc.id, { fee: v ?? 0, priced: true })}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <TablePagination
          pagination={pager}
          total={shown.length}
          loading={pricesLoading}
          unit="service"
        />
      </ListPageTable>
    </ListPage>
  );
}

function NumCell({
  value,
  onChange,
  onClear,
  disabled,
  className,
  placeholder,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  /** Emptying the box, when that means something other than "no change". */
  onClear?: () => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}) {
  const [local, setLocal] = useState<string>(value == null ? "" : String(value));
  useEffect(() => {
    setLocal(value == null ? "" : String(value));
  }, [value]);
  return (
    <Input
      type="number"
      step="0.01"
      min="0"
      className={cn("h-8 w-28 text-right text-xs", className)}
      value={local}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (local === "") {
          // Without a handler, an emptied box is a slip rather than an instruction,
          // so the number it held comes back.
          if (onClear) onClear();
          else setLocal(value == null ? "" : String(value));
          return;
        }
        const n = Number(local);
        if (!isNaN(n) && n !== value) onChange(n);
      }}
    />
  );
}
